import { useCallback, useRef, useState } from 'react';
import { useIsHandheld } from './useMediaQuery';
import { trackDesktopWallBypassed, trackDesktopWallShown } from '../services/analyticsService';

/**
 * THE SIGN-IN GATE for handheld visitors (RF3-DEVICE, owner 2026-08-22).
 *
 * The wall hangs on the sign-in ATTEMPT, never on page load. The public landing is where traffic arrives and
 * its showcase reels are 9:16 video that look their best on a phone, so blocking the page would cost us
 * acquisition to solve a problem that only starts after login. A visitor browses freely; the moment they
 * reach for a Google button on a handheld, we explain why the studio wants a bigger screen.
 *
 * It is a warning, not a lock. `continueAnyway` runs the exact action that was intercepted, so nobody is ever
 * shut out of their own account by a media query that guessed their device wrong.
 *
 * @param source names the surface for analytics (e.g. 'landing'). PII-free.
 */
export interface DesktopSignInWall {
  /** Whether the wall is currently raised. */
  open: boolean;
  /** Run `proceed` now on a workstation; on a handheld, hold it and raise the wall instead. */
  requestSignIn: (proceed: () => void) => void;
  /** The escape hatch — close the wall and run the held action after all. */
  continueAnyway: () => void;
  /** Closed without continuing (Escape, backdrop, the close control). The held action is dropped. */
  dismiss: () => void;
}

export function useDesktopSignInWall(source: string): DesktopSignInWall {
  const isHandheld = useIsHandheld();
  const [open, setOpen] = useState(false);
  // A ref, not state: the held action is not rendered, and keeping it out of state means raising the wall
  // costs exactly one re-render.
  const held = useRef<(() => void) | null>(null);

  const requestSignIn = useCallback((proceed: () => void) => {
    if (!isHandheld) {
      proceed();
      return;
    }
    held.current = proceed;
    setOpen(true);
    trackDesktopWallShown(source);
  }, [isHandheld, source]);

  const continueAnyway = useCallback(() => {
    setOpen(false);
    trackDesktopWallBypassed(source);
    const proceed = held.current;
    held.current = null;
    proceed?.();
  }, [source]);

  const dismiss = useCallback(() => {
    setOpen(false);
    held.current = null;
  }, []);

  return { open, requestSignIn, continueAnyway, dismiss };
}
